import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader, Loader, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtDate } from "@/lib/format";

export default function AdminSpin() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [activity, setActivity] = useState([]);
  const load = () => {
    api.get("/admin/spin/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/spin/activity").then(({ data }) => setActivity(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loader />;

  const setSeg = (i, key, val) => {
    const segments = [...cfg.segments];
    segments[i] = { ...segments[i], [key]: key === "value" ? Number(val) : val };
    setCfg({ ...cfg, segments });
  };
  const addSeg = () => setCfg({ ...cfg, segments: [...cfg.segments, { label: "10 Points", type: "points", value: 10 }] });
  const delSeg = (i) => setCfg({ ...cfg, segments: cfg.segments.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      await api.put("/admin/spin/config", { segments: cfg.segments, enabled: cfg.enabled, campaign_start: cfg.campaign_start, campaign_end: cfg.campaign_end });
      toast.success("Spin config saved"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={t("spin_control")} subtitle="Configure wheel rewards and daily rules." />
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <div className="ec-card p-6">
          <div className="flex items-center justify-between mb-5">
            <Label>Spin Enabled</Label>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="spin-enabled-switch" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div><Label>Campaign Start</Label><Input type="date" value={cfg.campaign_start || ""} onChange={(e) => setCfg({ ...cfg, campaign_start: e.target.value })} className="mt-1" data-testid="campaign-start" /></div>
            <div><Label>Campaign End</Label><Input type="date" value={cfg.campaign_end || ""} onChange={(e) => setCfg({ ...cfg, campaign_end: e.target.value })} className="mt-1" data-testid="campaign-end" /></div>
          </div>
          <Label className="mb-2 block">Wheel Segments</Label>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {cfg.segments.map((s, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`segment-${i}`}>
                <Input value={s.label} onChange={(e) => setSeg(i, "label", e.target.value)} className="flex-1" placeholder="Label" />
                <Select value={s.type} onValueChange={(v) => setSeg(i, "type", v)}>
                  <SelectTrigger className="w-28 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="points">Points</SelectItem><SelectItem value="try_again">Try Again</SelectItem></SelectContent>
                </Select>
                <Input type="number" value={s.value} onChange={(e) => setSeg(i, "value", e.target.value)} className="w-20" />
                <button onClick={() => delSeg(i)} className="text-red-400 p-1" data-testid={`del-seg-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <Button onClick={addSeg} variant="outline" className="mt-3 border-white/10 bg-white/5" data-testid="add-segment"><Plus className="w-4 h-4 me-1" />Add Segment</Button>
          <Button onClick={save} className="mt-4 w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold" data-testid="save-spin-config">{t("save")}</Button>
        </div>

        <div className="ec-card p-6">
          <h3 className="font-head text-lg font-semibold text-white mb-4">{t("spin_activity")}</h3>
          {activity.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No spins recorded.</p> : (
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
              {activity.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-3 text-sm">
                  <div><p className="text-white">{h.label}</p><p className="text-xs text-gray-500">{fmtDate(h.created_at)}</p></div>
                  <span className={h.points > 0 ? "text-emerald-400 font-semibold" : "text-gray-500"}>{h.points > 0 ? `+${h.points}` : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
